import { createElement, lazy } from "react";
import type { ComponentType, LazyExoticComponent, ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import { SignUpLayout } from "@/components/layout/SignUpLayout";
import { ROUTES } from "@/constants/routes";

const LazyRoutes = {
    NotFound: lazy(() => import("@/pages/NotFound")),
    Dashboard: lazy(() => import("@/pages/Dashboard")),
    Login: lazy(() => import("@/pages/Login")),
    OnboardingPage: lazy(() => import("@/pages/OnboardingPage")),
    CreateProject: lazy(() => import("@/pages/CreateProject")),
    EscrowAccount: lazy(() => import("@/pages/EscrowAccount")),
    TeamAccessControl: lazy(() => import("@/pages/TeamAccessControl")),
    NeedToDevelop: lazy(() => import("@/pages/NeedToDevelop")),
    Subscription: lazy(() => import("@/pages/Subscription")),
    Payment: lazy(() => import("@/pages/Payment")),
    Builder: lazy(() => import("@/pages/BuilderManagement")),
    BuilderManage: lazy(() => import("@/pages/BuilderManage")),
    ProjectCompliance: lazy(() => import("@/pages/ProjectCompliance")),
    EscrowPaymentControl: lazy(() => import("@/pages/EscrowPaymentsControl")),
    BuyerManagement: lazy(() => import("@/pages/BuyerManagement")),
    BuyerManage: lazy(() => import("@/pages/BuyerManage")),
    BuyerProfile: lazy(() => import("@/pages/BuyerFullProfile")),
    BrokerManagement: lazy(() => import("@/pages/BrokerManagement")),
    BrokerManage: lazy(() => import("@/pages/BrokerManage")),
    BrokerReassign: lazy(() => import("@/pages/BrokerReassignLeads")),
    BrokerManageCommission: lazy(() => import("@/pages/BrokerManageCommission")),
    LeadVisitTriage: lazy(() => import("@/pages/LeadVisitTriage")),
    Settings: lazy(() => import("@/pages/Settings")),
    CommunicationMonitoring: lazy(() => import("@/pages/CommunicationMonitoring")),
    DisputesIncidents: lazy(() => import("@/pages/DisputesIncidents")),
    BuilderProjects: lazy(() => import("@/pages/BuilderProjects")),
    BUILDERHISTORY: lazy(() => import("@/pages/BuilderHistory")),
    BuilderProfile: lazy(() => import("@/pages/BuilderProfile")),
    BuyerCommunications: lazy(() => import("@/pages/BuyerCommunications")),
    AuditReports: lazy(() => import("@/pages/AuditReports")),
};

export type PageComponent = LazyExoticComponent<ComponentType<any>> | typeof Outlet;

const AuthenticatedDefaultRoute = () =>
    createElement(Navigate, { to: ROUTES.DEFAULT_AUTHENTICATED, replace: true });

export interface RouteDefinition {
    path: string;
    component: PageComponent;
    children?: RouteDefinition[];
}

export interface LayoutGroup {
    layout: ComponentType<{ children: ReactNode }>;
    isPrivate: boolean;  // 👈 NEW
    routes: RouteDefinition[];
}

export const routesConfig: LayoutGroup[] = [
    {
        layout: AuthenticatedLayout,
        isPrivate: true,
        routes: [
            { path: "/", component: AuthenticatedDefaultRoute },
            { path: ROUTES.MAIN.DASHBOARD, component: LazyRoutes.Dashboard },
            { path: ROUTES.MAIN.CREATEPROJECT, component: LazyRoutes.CreateProject },
            { path: ROUTES.MAIN.ESCROW, component: LazyRoutes.EscrowAccount },
            { path: ROUTES.MAIN.TEAM, component: LazyRoutes.TeamAccessControl },
            { path: ROUTES.MAIN.SUBSCRIPTION, component: LazyRoutes.Subscription },
            { path: ROUTES.MAIN.PAYMENT, component: LazyRoutes.Payment },
            { path: ROUTES.MAIN.BUILDER, component: LazyRoutes.Builder },
            { path: ROUTES.MAIN.BUILDERMANAGE, component: LazyRoutes.BuilderManage },
            { path: ROUTES.MAIN.PROJECTCOMPLIANCE, component: LazyRoutes.ProjectCompliance },
            { path: ROUTES.MAIN.ESCROWPAYMENT, component: LazyRoutes.EscrowPaymentControl },
            { path: ROUTES.MAIN.BUYER, component: LazyRoutes.BuyerManagement },
            { path: ROUTES.MAIN.BUYERMANAGE, component: LazyRoutes.BuyerManage },
            { path: ROUTES.MAIN.BROKER, component: LazyRoutes.BrokerManagement },
            { path: ROUTES.MAIN.BROKERMANAGE, component: LazyRoutes.BrokerManage },
            { path: ROUTES.MAIN.BROKERRESSIGN, component: LazyRoutes.BrokerReassign },
            { path: ROUTES.MAIN.BUILDERHISTORY, component: LazyRoutes.BUILDERHISTORY },
            { path: ROUTES.MAIN.LeadVisitTriage, component: LazyRoutes.LeadVisitTriage },
            { path: ROUTES.BOTTOM.SETTINGS, component: LazyRoutes.Settings },
            { path: ROUTES.MAIN.Communication, component: LazyRoutes.CommunicationMonitoring },
            { path: ROUTES.MAIN.Disputes, component: LazyRoutes.DisputesIncidents },
            { path: ROUTES.MAIN.BUILDERPROJECTS, component: LazyRoutes.BuilderProjects },
            { path: ROUTES.MAIN.BUYERCOMMUNICATION, component: LazyRoutes.BuyerCommunications },
            { path: ROUTES.MAIN.BUILDERPROFILE, component: LazyRoutes.BuilderProfile },
            { path: ROUTES.MAIN.AUDITREPORTS, component: LazyRoutes.AuditReports },
            { path: ROUTES.MAIN.BROKERCOMMISSON, component: LazyRoutes.BrokerManageCommission },
            { path: ROUTES.MAIN.BUYERPROFILE, component: LazyRoutes.BuyerProfile },

            // TODO: Enable the Componets When UI is ready
            { path: ROUTES.MAIN.ANALYTICS, component: LazyRoutes.NeedToDevelop },
            { path: ROUTES.MAIN.LEADS, component: LazyRoutes.NeedToDevelop },
            { path: ROUTES.MAIN.BANK, component: LazyRoutes.NeedToDevelop },
            { path: ROUTES.MAIN.INQUIRIES, component: LazyRoutes.NeedToDevelop },
            { path: ROUTES.MAIN.WALLET, component: LazyRoutes.NeedToDevelop },
            { path: ROUTES.MAIN.GALLERY, component: LazyRoutes.NeedToDevelop },
            { path: ROUTES.BOTTOM.INFO, component: LazyRoutes.NeedToDevelop },


            { path: "*", component: LazyRoutes.NotFound },
        ],
    },
    {
        layout: SignUpLayout,
        isPrivate: false, // 🔓 Public pages only
        routes: [
            { path: ROUTES.AUTH.LOGIN, component: LazyRoutes.Login },
            { path: ROUTES.AUTH.SIGNUP, component: LazyRoutes.OnboardingPage },
            { path: "*", component: LazyRoutes.NotFound },
        ],
    },
];
